import type { DependencyEdge, DependencyFacts } from './api';

/**
 * Where the dependency picture's geometry is decided, away from the SVG that draws it.
 *
 * The charter's standing decision puts derived FACTS on the server and leaves the picture
 * to be drawn by hand here. Everything in this file is the picture: which links are worth
 * drawing, which column and row each ticket sits in, and where that lands in pixels. It
 * states no fact about the work that `/v1/epics/{name}` does not already return: the two
 * it would otherwise have invented — how deep a ticket sits and which chain sets the
 * epic's length — are the server's `chainDepth` and `criticalPath`, grouped and
 * highlighted here but never recomputed, so the page and an agent reading the MCP cannot
 * disagree about what is blocked.
 *
 * Kept React-free so the layering and ordering can be tested without a DOM. DWLF-191.
 */

export const NODE_WIDTH = 116;
export const NODE_HEIGHT = 32;
export const COLUMN_GAP = 96;
export const ROW_GAP = 16;
export const PADDING = 14;

export interface PositionedTicket {
  ref: number;
  column: number;
  row: number;
  x: number;
  y: number;
}

export interface GraphLayout {
  tickets: PositionedTicket[];
  width: number;
  height: number;
}

export interface WaveGroup {
  /** Rounds of unfinished work standing in front of these tickets, or null off-scope. */
  depth: number | null;
  refs: number[];
}

const ordered = (refs: Iterable<number>) => [...refs].sort((a, b) => a - b);

const isBlocking = (edge: DependencyEdge) => edge.kind === 'blocks';

/** ref -> the tickets that must finish before it, for `blocks` edges only. */
function blockersByRef(edges: DependencyEdge[]): Map<number, number[]> {
  const blockers = new Map<number, number[]>();
  for (const edge of edges.filter(isBlocking)) {
    blockers.set(edge.to, [...(blockers.get(edge.to) ?? []), edge.from]);
  }
  return blockers;
}

/** ref -> the tickets waiting on it, for `blocks` edges only. */
function dependentsByRef(edges: DependencyEdge[]): Map<number, number[]> {
  const dependents = new Map<number, number[]>();
  for (const edge of edges.filter(isBlocking)) {
    dependents.set(edge.from, [...(dependents.get(edge.from) ?? []), edge.to]);
  }
  return dependents;
}

export const refsIn = (edges: DependencyEdge[]): number[] =>
  ordered(new Set(edges.flatMap((edge) => [edge.from, edge.to])));

/**
 * Walk a DAG-shaped map, memoised, refusing to recurse into a ref already on the stack.
 *
 * Two `blocks` links written in opposite directions make a cycle, which nothing prevents.
 * A cycle must not hang the render, so a ref already being visited contributes nothing —
 * but a value a cycle truncated is NOT cached, because that value is an artefact of where
 * the walk started and caching it would make the same graph lay out differently depending
 * on which ref the outer loop reached first.
 */
function memoisedDepth(edgesTo: Map<number, number[]>): (ref: number) => number {
  const settled = new Map<number, number>();
  const visiting = new Set<number>();

  const walk = (ref: number): { value: number; truncated: boolean } => {
    const cached = settled.get(ref);
    if (cached !== undefined) return { value: cached, truncated: false };
    if (visiting.has(ref)) return { value: 0, truncated: true };

    visiting.add(ref);
    const behind = (edgesTo.get(ref) ?? []).map(walk);
    visiting.delete(ref);

    const value = behind.length === 0 ? 0 : 1 + Math.max(...behind.map((step) => step.value));
    const truncated = behind.some((step) => step.truncated);
    if (!truncated) settled.set(ref, value);
    return { value, truncated };
  };

  return (ref) => walk(ref).value;
}

/**
 * Drop `blocks` edges that another path already implies.
 *
 * If A blocks C and A also reaches C through B, the direct line adds a stroke and no
 * constraint. The edge stays in the board's data — it is how somebody recorded intent —
 * but repeating it in the drawing only costs crossings. Removing implied edges cannot
 * change any ticket's column, because the longer path that implied it still sets it.
 *
 * ⚠️ The alternative path may not run through a loop. Asking each edge independently
 * "does another route get there?" is sound in a DAG and wrong the moment a `blocks` cycle
 * exists: with 2 and 3 blocking each other, 1→2 looks implied by 1→3→2 and 1→3 looks
 * implied by 1→2→3, so BOTH go and ticket 1 leaves the drawing altogether. That is the
 * default view, so it would fail silently rather than loudly.
 */
export function withoutImpliedEdges(edges: DependencyEdge[]): DependencyEdge[] {
  const blocking = edges.filter(isBlocking);

  const adjacency = (from: DependencyEdge[]) => {
    const next = new Map<number, Set<number>>();
    for (const edge of from) {
      if (!next.has(edge.from)) next.set(edge.from, new Set());
      next.get(edge.from)?.add(edge.to);
    }
    return next;
  };

  const reaches = (next: Map<number, Set<number>>, start: number, skipped?: DependencyEdge) => {
    const seen = new Set<number>();
    const pending = [start];
    while (pending.length > 0) {
      const at = pending.pop() as number;
      for (const to of next.get(at) ?? []) {
        if (skipped && at === skipped.from && to === skipped.to) continue;
        if (seen.has(to)) continue;
        seen.add(to);
        pending.push(to);
      }
    }
    return seen;
  };

  // An edge whose head can get back to its tail is part of a loop. Those are the routes
  // that make a genuine link look redundant, so the alternative path may not use them.
  const whole = adjacency(blocking);
  const onALoop = (edge: DependencyEdge) => reaches(whole, edge.to).has(edge.from);
  const forward = adjacency(blocking.filter((edge) => !onALoop(edge)));

  return edges.filter(
    (edge) => !isBlocking(edge) || !reaches(forward, edge.from, edge).has(edge.to),
  );
}

export interface EdgeSelection {
  showRelatesTo: boolean;
  hideImpliedEdges: boolean;
  /**
   * Consecutive pairs here survive the implied-edge reduction.
   *
   * The server reckons the critical path over UNFINISHED blockers while the drawing
   * reduces over every `blocks` edge, so where A → Done → B sits beside A → B the direct
   * link is implied and dropped — leaving two highlighted boxes with no highlighted line
   * between them. That gap grows as tickets close, which is the wrong way round.
   */
  keepChain?: number[];
}

const pairKey = (from: number, to: number) => `${from}:${to}`;

/**
 * The links worth drawing under the panel's current settings.
 *
 * `relates-to` is context and gates nothing, so it is off by default: the panel answers
 * "what order can this be done in", and a line that constrains no order is noise in that
 * answer. It also drags in tickets that have no `blocks` link at all, which is why the
 * drawn population changes with the toggle rather than only the line count.
 */
export function selectEdges(edges: DependencyEdge[], selection: EdgeSelection): DependencyEdge[] {
  const all = edges.filter(isBlocking);
  const chain = selection.keepChain ?? [];
  const onChain = new Set(chain.slice(0, -1).map((ref, at) => pairKey(ref, chain[at + 1])));
  const kept = all.filter((edge) => onChain.has(pairKey(edge.from, edge.to)));

  const blocking = selection.hideImpliedEdges
    ? [...new Set([...withoutImpliedEdges(all), ...kept])]
    : all;
  const context = selection.showRelatesTo ? edges.filter((edge) => !isBlocking(edge)) : [];
  return [...blocking, ...context];
}

/**
 * Column per ticket: how long the chain of blockers behind it runs.
 *
 * This is LAYOUT and it deliberately counts FINISHED blockers too — a completed ticket
 * still belongs to the left of the one it unblocked. The server's `chainDepth` counts
 * only what is left to do, so the two are expected to differ; `waveGroups` is where the
 * server's number is used, and it is the one shown to a reader as a number.
 */
function columnsByLongestChain(edges: DependencyEdge[], refs: number[]): Map<number, number> {
  const depthOf = memoisedDepth(blockersByRef(edges));
  return new Map(refs.map((ref) => [ref, depthOf(ref)]));
}

/**
 * Slide every ticket with no blocker rightwards, to just before the first thing it holds up.
 *
 * Longest-chain layering alone puts everything unblocked in column 0, which on a real epic
 * is most of the graph: the picture becomes one tall stack feeding a few short columns, and
 * every edge out of it travels the height of the drawing. A ticket with no blocker is free
 * to sit anywhere left of its dependents, so putting it as close to them as possible costs
 * no truth and shortens the lines.
 */
function packSourcesRight(
  columns: Map<number, number>,
  edges: DependencyEdge[],
  refs: number[],
): Map<number, number> {
  const dependents = dependentsByRef(edges);
  const isBlocked = new Set(edges.filter(isBlocking).map((edge) => edge.to));
  const packed = new Map(columns);

  for (const ref of refs) {
    if (isBlocked.has(ref)) continue;
    const waiting = dependents.get(ref);
    if (!waiting || waiting.length === 0) continue;
    const earliest = Math.min(...waiting.map((dependent) => columns.get(dependent) ?? 0));
    packed.set(ref, earliest - 1);
  }

  const leftmost = Math.min(...packed.values());
  return new Map([...packed].map(([ref, column]) => [ref, column - leftmost]));
}

const CROSSING_SWEEPS = 24;

/**
 * Order the tickets within each column so fewer lines cross.
 *
 * Sorting rows by ref — which is filing order, and says nothing about the graph — leaves
 * the one phase of a layered drawing whose entire job is reducing crossings doing nothing.
 * This is the median heuristic: repeatedly place each ticket beside the median position of
 * its neighbours in the column it was just compared against, alternating direction so both
 * ends of every edge get a turn. It is a heuristic, not an optimum, and it must stay
 * deterministic — hence the fixed sweep count and the ref tie-break on every sort.
 */
function rowsByMedianOfNeighbours(
  columns: Map<number, number>,
  edges: DependencyEdge[],
  refs: number[],
): Map<number, number> {
  const byColumn = new Map<number, number[]>();
  for (const ref of refs) {
    const column = columns.get(ref) ?? 0;
    byColumn.set(column, [...(byColumn.get(column) ?? []), ref]);
  }
  const columnOrder = [...byColumn.keys()].sort((a, b) => a - b);
  for (const column of columnOrder) byColumn.get(column)?.sort((a, b) => a - b);

  const positions = () => {
    const at = new Map<number, number>();
    for (const column of columnOrder) {
      byColumn.get(column)?.forEach((ref, index) => at.set(ref, index));
    }
    return at;
  };

  const blockers = blockersByRef(edges);
  const dependents = dependentsByRef(edges);
  const medianOfNeighbours = (ref: number, from: Map<number, number[]>, at: Map<number, number>) => {
    const neighbours = (from.get(ref) ?? [])
      .map((neighbour) => at.get(neighbour))
      .filter((index): index is number => index !== undefined)
      .sort((a, b) => a - b);
    if (neighbours.length === 0) return null;
    const middle = neighbours.length >> 1;
    return neighbours.length % 2 === 1
      ? neighbours[middle]
      : (neighbours[middle - 1] + neighbours[middle]) / 2;
  };

  for (let sweep = 0; sweep < CROSSING_SWEEPS; sweep += 1) {
    const forwards = sweep % 2 === 0;
    const sweepOrder = forwards ? columnOrder : [...columnOrder].reverse();
    const lookAt = forwards ? blockers : dependents;
    for (const column of sweepOrder) {
      const at = positions();
      const inColumn = byColumn.get(column) ?? [];
      const key = new Map(
        inColumn.map((ref) => [ref, medianOfNeighbours(ref, lookAt, at) ?? at.get(ref) ?? 0]),
      );
      inColumn.sort((a, b) => (key.get(a) ?? 0) - (key.get(b) ?? 0) || a - b);
    }
  }

  const rows = new Map<number, number>();
  for (const column of columnOrder) {
    byColumn.get(column)?.forEach((ref, index) => rows.set(ref, index));
  }
  return rows;
}

/** Place every ticket the given links touch. Deterministic: same links, same picture. */
export function layoutGraph(edges: DependencyEdge[]): GraphLayout {
  const refs = refsIn(edges);
  if (refs.length === 0) return { tickets: [], width: 0, height: 0 };

  const columns = packSourcesRight(columnsByLongestChain(edges, refs), edges, refs);
  const rows = rowsByMedianOfNeighbours(columns, edges, refs);

  const tickets = refs.map((ref) => {
    const column = columns.get(ref) ?? 0;
    const row = rows.get(ref) ?? 0;
    return {
      ref,
      column,
      row,
      x: PADDING + column * (NODE_WIDTH + COLUMN_GAP),
      y: PADDING + row * (NODE_HEIGHT + ROW_GAP),
    };
  });

  return {
    tickets,
    width: Math.max(...tickets.map((ticket) => ticket.x)) + NODE_WIDTH + PADDING,
    height: Math.max(...tickets.map((ticket) => ticket.y)) + NODE_HEIGHT + PADDING,
  };
}

/**
 * One ticket, everything that must finish before it, and everything waiting on it.
 *
 * `relates-to` is excluded whatever the panel's toggle says: this answers "what is the
 * chain through this ticket", and a link that gates nothing is not on any chain.
 */
export function chainThrough(edges: DependencyEdge[], ref: number): Set<number> {
  const reach = (from: Map<number, number[]>) => {
    const seen = new Set<number>();
    const pending = [ref];
    while (pending.length > 0) {
      const at = pending.pop() as number;
      for (const next of from.get(at) ?? []) {
        if (seen.has(next)) continue;
        seen.add(next);
        pending.push(next);
      }
    }
    return seen;
  };

  return new Set([ref, ...reach(blockersByRef(edges)), ...reach(dependentsByRef(edges))]);
}

/**
 * The drawn tickets grouped by how many rounds of unfinished work stand in front of them.
 *
 * The number is the server's `chainDepth`, not a recomputation — it is a fact about the
 * work rather than about the picture, so the page must report exactly what `epics_get`
 * reports. A ticket the server gave no depth for is one belonging to another epic, which
 * is outside the scope `chainDepth` was computed over; it groups under `null` rather than
 * being given a number this page invented or being dropped from a view the others show.
 */
export function waveGroups(refs: number[], chainDepth: DependencyFacts['chainDepth']): WaveGroup[] {
  const byDepth = new Map<number | null, number[]>();
  for (const ref of ordered(refs)) {
    const depth = chainDepth[ref] ?? null;
    byDepth.set(depth, [...(byDepth.get(depth) ?? []), ref]);
  }

  return [...byDepth.entries()]
    .map(([depth, group]) => ({ depth, refs: group }))
    .sort((a, b) => {
      if (a.depth === null) return 1;
      if (b.depth === null) return -1;
      return a.depth - b.depth;
    });
}
